/**
 * Hook: useTrackPageLoad
 *
 * Purpose:
 * - Tracks page load performance metrics and sends a Snowplow DurationEvent.
 * - Flushes any pending GraphQL DurationEvents once the page load completes.
 * - Attaches PageEntity and ConditionEntities to all page load and GraphQL events.
 * - Sets a "page load complete" flag for downstream modules (e.g. timingLink).
 *
 * How it works:
 * - On route change, records navigation start time.
 * - Listens to network request activity (via `onNetworkChange`).
 * - When network becomes idle, sends a page load event with accurate timing.
 * - If no network activity occurs within 5 seconds, sends a fallback page load.
 * - Flushes any saved GraphQL query durations immediately after.
 * - Marks the page as fully loaded so later GraphQL events are logged immediately.
 *
 * Notes:
 * - Debounces network idle detection by 300ms to avoid false positives.
 * - Automatically cancels timers and listeners on unmount.
 * - `pageLoadCompleteRef` is shared externally to time GraphQL events correctly.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { onNetworkChange } from '../helpers/networkTracker';
import { flushPendingTimingEvents } from '../helpers/timingLink';
import { findPageContext } from '../helpers/findPageContext';
import { buildConditionEntitiesFromUrl } from '../helpers/buildConditionEntitiesFromUrl';
import { pageLoadCompleteRef } from '../helpers/pageLoadState';
import uiStructure from '../../apollo/uiStructure';

const { getSnowplowTracker, HudlDataSchemas } = require('@hudl/analytics');

const DEBOUNCE_MS = 300;
const FALLBACK_MS = 5000;
const FALLBACK_DURATION_MS = 1000;

export const useTrackPageLoad = () => {
  const location = useLocation();
  const startRef = useRef(null);
  const debounceTimeout = useRef(null);
  const fallbackTimeout = useRef(null);
  const unsubscribeRef = useRef(null);
  const didSendRequest = useRef(false);

  useEffect(() => {
    startRef.current = performance.now();
    didSendRequest.current = false;
    // console.log('[PageLoad] Navigation started:', location.pathname);
    pageLoadCompleteRef.current = false;

    const sendPageLoadEvent = async ({ fallback = false } = {}) => {
      clearTimeout(fallbackTimeout.current);

      let startTime;
      let endTime;
      let duration;

      if (fallback) {
        // Fallback mode: simulate a 1s page load
        const now = Date.now();
        startTime = new Date(now - FALLBACK_DURATION_MS).toISOString();
        endTime = new Date(now).toISOString();
        duration = FALLBACK_DURATION_MS;
      } else {
        // Normal page load timing using performance.now()
        const end = performance.now();
        duration = Math.round(end - startRef.current);

        if (duration > DEBOUNCE_MS) {
          duration -= DEBOUNCE_MS;
        }

        startTime = new Date(
          performance.timeOrigin + startRef.current,
        ).toISOString();
        endTime = new Date(performance.timeOrigin + end).toISOString();
      }

      // Build page contexts (PageEntity + ConditionEntities)
      const { page_type, page_sub_type } = findPageContext(location.pathname);
      const pageContexts = [
        HudlDataSchemas.PageEntity.V2_0_2.describe({
          page_url: window.location.href,
          page_type,
          page_sub_type,
          generic_page_url: location.pathname,
        }),
        ...buildConditionEntitiesFromUrl(location.pathname, uiStructure),
      ];

      const payload = HudlDataSchemas.DurationEvent.V1_0_0.describe({
        event_name: 'page_load',
        event_desc: location.pathname,
        start_timestamp: startTime,
        end_timestamp: endTime,
        duration_ms: duration,
      });
      try {
        const tracker = getSnowplowTracker('hudlIQ');
        // eslint-disable-next-line no-underscore-dangle
        const existingContexts = tracker?._globalContexts?.hudlIQ || [];
        const PRESERVED_REGEXES = [
          /^iglu:com\.hudl\/application_entity\/jsonschema\/\d-\d-\d$/,
          /^iglu:com\.hudl\/user_org_entity\/jsonschema\/\d-\d-\d$/,
          /^iglu:com\.hudl\/user_entity\/jsonschema\/\d-\d-\d$/,
        ];
        const getContextKey = (ctx) => ctx?.schema || JSON.stringify(ctx);
        const preservedContexts = existingContexts.filter((ctx) =>
          PRESERVED_REGEXES.some((regex) => regex.test(getContextKey(ctx))),
        );
        const preservedKeys = new Set(preservedContexts.map(getContextKey));
        const newPageContexts = pageContexts.filter(
          (ctx) => !preservedKeys.has(getContextKey(ctx)),
        );
        // Merge preserved (user/app) and new page contexts
        const mergedContexts = [...preservedContexts, ...newPageContexts];
        await tracker?.removeGlobalContexts('hudlIQ'); // Clear existing contexts
        await tracker?.addGlobalContexts('hudlIQ', preservedContexts); // Attach contexts
        await tracker?.trackSelfDescribingEvent(payload, pageContexts);
        flushPendingTimingEvents(mergedContexts); // Pass pageContexts to GraphQL flush
        pageLoadCompleteRef.current = true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[PageLoad] Failed to track page load event:', err);
      }
    };

    const debouncedFire = () => {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        sendPageLoadEvent(); // Fire after debounce when network idle
        unsubscribeRef.current?.();
      }, DEBOUNCE_MS);
    };

    unsubscribeRef.current = onNetworkChange((active) => {
      // console.log('[PageLoad] Active network requests:', active);

      if (active > 0) {
        didSendRequest.current = true; // Detected network requests
      }

      if (active === 0) {
        debouncedFire(); // Network idle
      } else {
        clearTimeout(debounceTimeout.current);
      }
    });

    fallbackTimeout.current = setTimeout(() => {
      if (!didSendRequest.current) {
        // console.log('[PageLoad] Fallback triggered — no network requests detected');
        sendPageLoadEvent({ fallback: true }); // Fallback if no API requests after 5s
        unsubscribeRef.current?.();
      } else {
        // console.log('[PageLoad] Fallback cancelled — API requests were sent');
      }
    }, FALLBACK_MS);

    return () => {
      // Cleanup on route change or unmount
      clearTimeout(debounceTimeout.current);
      clearTimeout(fallbackTimeout.current);
      unsubscribeRef.current?.();
    };
  }, [location]);
};
